import React, { useCallback } from 'react';
import classes from '~/app/common/scss/checkout.module.scss';
import { useStoreActions, useStoreState } from '~/store';
import Modal from '~/app/common/Modal';
import Icon from '~/app/common/Icon';
import Button from '~/app/common/Button';
import { SaveMaterialResponse } from '~/types';
import useTranslation from '~/app/hooks/useTranslation';
import { PartLinkTypes } from '~/app/data/statuses/issue';
import { formatMaterialPrice, getMaterialStoreKey, isVariousMaterial, toSaveMaterialPayload } from '~/materials/helper';

export interface CheckoutProps {
  open: boolean
  onClose: () => void
  onCloseAll: () => void
  type: PartLinkTypes
}
export type CheckoutComponent = React.FC<CheckoutProps>

const Checkout: CheckoutComponent = ({ open, onClose, onCloseAll, type }) => {
  const basePath = getMaterialStoreKey(type);
  const id = useStoreState(state => state[basePath].data.id);
  const tempMaterials = useStoreState(state => state[basePath].tempMaterials ?? []);
  const saveMaterialsAction = useStoreActions(actions => actions[basePath].saveMaterials);
  const fetchMaterialsAction = useStoreActions(actions => actions[basePath].fetchMaterials);

  const { t } = useTranslation('parts');

  const visibleMaterials = tempMaterials.filter(m => !m.deleted);
  const totalPrice = visibleMaterials.reduce(
    (sum, material) => sum + (Number(material.pricePerUnit) || 0) * (Number(material.quantity) || 0),
    0
  );

  const handleSave = useCallback(() => {
    saveMaterialsAction(toSaveMaterialPayload(id, tempMaterials)).then((res: SaveMaterialResponse) => {
      if (res.success) {
        fetchMaterialsAction(String(id));
        onCloseAll();
      }
    });
  }, [saveMaterialsAction, fetchMaterialsAction, id, tempMaterials, onCloseAll]);

  return (
    <Modal className={classes['checkout']} open={open} onClose={onClose} position={'bottom'}>
      <div className={classes['partManager']}>
        <div className={classes['partsHeader']}>
          <span>{t('reviewMaterials')}</span>
          <Icon name="close" onClick={onClose} />
        </div>
        <div className={classes['partsList']}>
          {visibleMaterials.map((material) => {
            const displayName = isVariousMaterial(material)
              ? t('variousMaterial')
              : (material.name ?? '');
            const lineTotal = (Number(material.pricePerUnit) || 0) * (Number(material.quantity) || 0);
            return (
              <div className={classes['partItem']} key={String(material.id)}>
                <div className={classes['column']}>
                  <span className={classes['partName']}>{displayName}</span>
                  <span className={classes['partNumber']}>
                    {material.categoryName
                      ? `${material.categoryName} · `
                      : ''}
                    {material.quantity} {t('item')}
                  </span>
                </div>
                <span className={classes['linePrice']}>{formatMaterialPrice(lineTotal)}</span>
              </div>
            );
          })}
        </div>
        <div className={classes['totalRow']}>
          <span>{t('total')}</span>
          <span className={classes['totalPrice']}>{formatMaterialPrice(totalPrice)}</span>
        </div>
        <Button className={classes['saveButton']} onClick={handleSave}>{t('saveMaterials')}</Button>
      </div>
    </Modal>
  );
};

export default Checkout;
